import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { AD_CONFIG, isAdAllowedOnRoute } from '../../config/adConfig';
import AdSenseBanner from './AdSenseBanner';
import CustomBanner from './CustomBanner';

/**
 * Universal Ad Banner Component
 * Automatically selects between AdSense and Custom ads
 * Handles route-based ad visibility
 * 
 * @param {string} placement - Ad placement (topBanner, sidebarBanner, bottomSticky, inContent)
 * @param {string} className - Additional CSS classes
 */
const AdBanner = ({ placement = 'topBanner', className = '', ...props }) => {
  const location = useLocation();
  
  // Check if ads are allowed on current route
  if (!isAdAllowedOnRoute(location.pathname)) {
    return null;
  }

  // Get placement config
  const placementConfig = AD_CONFIG.slots[placement];
  
  // Check if this placement is enabled
  if (!placementConfig || !placementConfig.enabled) {
    return null;
  }

  // Render based on provider
  const renderAd = () => {
    // Respect user consent: do not render ads until user grants permission
    let consent = null;
    if (typeof window !== 'undefined') {
      try { consent = localStorage.getItem('ads_consent'); } catch (e) { consent = null; }
    }
    if (AD_CONFIG.provider === 'google-adsense' && consent !== 'granted') {
      // In development show a placeholder; in production show nothing
      if (process.env.NODE_ENV === 'development') {
        return (
          <Box sx={{ width: '100%', minHeight: placementConfig.style?.minHeight || 90, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', border: '1px dashed', borderColor: 'grey.300' }}>
            <Typography variant="caption" color="text.secondary">Ad (consent required)</Typography>
          </Box>
        );
      }
      return null;
    }
    switch (AD_CONFIG.provider) {
      case 'google-adsense':
        return (
          <AdSenseBanner
            slot={placementConfig.slot}
            format={placementConfig.format}
            responsive={placementConfig.responsive}
            style={placementConfig.style}
          />
        );

      case 'custom':
        const customAd = AD_CONFIG.customAds[placement];
        if (!customAd) return null;
        
        return (
          <CustomBanner
            imageUrl={customAd.imageUrl}
            link={customAd.link}
            alt={customAd.alt}
            style={placementConfig.style}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box
      className={`ad-wrapper ad-${placement} ${className}`}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: placementConfig.sticky ? 'sticky' : 'relative',
        bottom: placementConfig.sticky ? placementConfig.stickyOffset : 'auto',
        zIndex: placementConfig.sticky ? 1000 : 'auto',
        backgroundColor: placementConfig.sticky ? 'background.paper' : 'transparent',
        boxShadow: placementConfig.sticky ? '0 -2px 10px rgba(0,0,0,0.1)' : 'none',
        ...props.sx,
      }}
    >
      {/* Ad Content */}
      {renderAd()}
    </Box>
  );
};

export default AdBanner;
